import { SyntheticEvent, useCallback, useState } from "react";
import { Concept, Pack } from "../Types";

type StateUpdater<S> = (value: S | ((prevState: S) => S)) => void;

// const AI_NEW_URL = "https://google.com/";
// const AI_FILL_URL = "https://google.com/";
const AI_NEW_URL = "https://api.optimem.org/ai/new";
const AI_FILL_URL = "https://api.optimem.org/ai/fill";
const AI_NEW_URL_LOCAL = "http://localhost:8080/ai/new";
const AI_FILL_URL_LOCAL = "http://localhost:8080/ai/fill";

type Props = {
  pack: Pack;
  suggestedNewConcepts: any[];
  setSuggestedNewConcepts: StateUpdater<any[]>;

  filledExistingConcepts: any[];
  setFilledExistingConcepts: StateUpdater<any[]>;

  setConcepts: (concepts: Concept[]) => void;
};

function getCompressedPack(pack: Pack): Pack {
  // Compress out redundant tags (AI doesn't necessarily need to know whether it generated these!)
  const compressedPack = { ...pack };
  compressedPack.data.concepts = pack.data.concepts.map((concept) => {
    const compressedConcept = { ...concept };
    if (compressedConcept.tags) {
      compressedConcept.tags = concept.tags!.filter((tag) =>
        tag !== "#aigen" && tag !== "#aigen-pending"
      );
      if (compressedConcept.tags.length == 0) {
        delete compressedConcept.tags;
      }
    }
    return compressedConcept;
  });
  return compressedPack;
}

export default function AISuggestor(props: Props) {
  const {
    pack,
    suggestedNewConcepts,
    setSuggestedNewConcepts,
    filledExistingConcepts,
    setFilledExistingConcepts,
    setConcepts,
  } = props;

  const [suggestedConceptsLoading, setSuggestedConceptsLoading] = useState(
    false,
  );
  const [suggestedConceptsError, setSuggestedConceptsError] = useState(false);

  const [server, setServer] = useState("optimem");
  const [creativity, setCreativity] = useState("low");
  const [quantity, setQuantity] = useState("few");
  const [key, setKey] = useState("");

  const onSelectCreativity = useCallback(
    (e: SyntheticEvent) => {
      setCreativity((e.target as HTMLInputElement).value);
    },
    [],
  );
  
  const onSelectServer = useCallback(
    (e: SyntheticEvent) => {
      setServer((e.target as HTMLInputElement).value);
    },
    [],
  );

  const onSelectQuantity = useCallback(
    (e: SyntheticEvent) => {
      setQuantity((e.target as HTMLInputElement).value);
    },
    [],
  );

  const onChangeKeyText = useCallback((
    e: SyntheticEvent<HTMLInputElement>,
  ) => {
    setKey((e.target as HTMLInputElement).value);
  }, [setKey]);

  const acceptSuggestedConcepts = useCallback(() => {
    if (suggestedNewConcepts.length > 0) {
      const newSuggestedConcepts = suggestedNewConcepts.map((concept) => ({
        ...concept,
        tags: concept.tags.filter((tag) => tag !== "#aigen-pending"),
      }));
      const joinedConcepts: Concept[] = [
        ...pack.data.concepts,
        ...newSuggestedConcepts,
      ];
      setConcepts(joinedConcepts);
      setSuggestedNewConcepts([]);
    } else if (filledExistingConcepts.length > 0) {
      const newFilledExistingConcepts = filledExistingConcepts.map((
        concept,
      ) => ({
        ...concept,
        tags: concept.tags.filter((tag) => !tag.startsWith("#aigen-")),
      }));

      const joinedConcepts: Concept[] = [...pack.data.concepts].map((x) => {
        const filled = newFilledExistingConcepts.find((y) => y.id === x.id);
        if (filled) {
          return filled;
        } else {
          return x;
        }
      });

      setConcepts(joinedConcepts);
      setFilledExistingConcepts([]);
    }
  }, [
    suggestedNewConcepts,
    suggestedNewConcepts.length,
    filledExistingConcepts,
    filledExistingConcepts.length,
    pack.data.concepts,
    pack.data.concepts.length,
  ]);

  const rejectSuggestedConcepts = useCallback(() => {
    setSuggestedNewConcepts([]);
    setFilledExistingConcepts([]);
  }, [
    suggestedNewConcepts,
    suggestedNewConcepts.length,
    filledExistingConcepts,
    filledExistingConcepts.length,
    pack.data.concepts,
    pack.data.concepts.length,
  ]);

  const getNewConcepts = useCallback(async () => {
    setSuggestedConceptsLoading(true);
    setSuggestedConceptsError(false);

    // Compress out redundant tags (AI doesn't necessarily need to know whether it generated these!)
    const compressedPack = { ...pack };
    compressedPack.data.concepts = pack.data.concepts.map((concept) => {
      const compressedConcept = { ...concept };
      if (compressedConcept.tags) {
        compressedConcept.tags = (concept.tags || []).filter((tag) =>
          tag !== "#aigen" && tag !== "#aigen-pending"
        );
        if (compressedConcept.tags.length == 0) {
          delete compressedConcept.tags;
        }
      }
      return compressedConcept;
    });

    try {
      const body = JSON.stringify({
        pack: compressedPack,
        settings: {
          creativity,
          quantity,
          key,
        },
      });

      console.log("sending", body);

      const response = await fetch(
        server === 'optimem' ? AI_NEW_URL : AI_NEW_URL_LOCAL,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
          },
          body,
        },
      );
      let data = await response.json();

      // if is array, then we got some suggestions
      if (Array.isArray(data)) {
        data = data.map((x) => ({
          ...x,
          tags: [
            ...(x.tags || []).filter((x) =>
              x !== "#aigen" && x !== "#aigen-pending"
            ),
            "#aigen",
            "#aigen-pending",
          ],
        }));
        console.log("suggested concepts", data);
        setSuggestedNewConcepts(data);
        setSuggestedConceptsError(false);
      } else {
        console.log("error getting concepts", data);
        setSuggestedConceptsError(true);
      }
    } catch (e) {
      setSuggestedConceptsError(true);
    }
    setSuggestedConceptsLoading(false);
  }, [server, pack, creativity, quantity, key, setSuggestedNewConcepts]);

  const getFillConcepts = useCallback(async () => {
    console.log("get concepts");
    setSuggestedConceptsLoading(true);
    setSuggestedConceptsError(false);

    const compressedPack = getCompressedPack(pack);

    try {
      const body = JSON.stringify({
        pack: compressedPack,
        settings: {
          creativity,
          quantity,
          key,
        },
      });

      console.log("sending", body);

      const response = await fetch(
        server === 'optimem' ? AI_FILL_URL : AI_FILL_URL_LOCAL,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
          },
          body,
        },
      );
      let data = await response.json();
      console.log("suggested concepts", data);

      // if is array, then we got some suggestions
      if (Array.isArray(data)) {
        data = data.map((x) => {
          const existingX = pack.data.concepts.find((c) => c.id === x.id);
          const newX = {
            ...x,
            tags: [
              ...(x.tags || []).filter((x) =>
                x !== "#aigen" && x !== "#aigen-pending"
              ),
              "#aigen",
              "#aigen-pending",
            ],
          };
          const repKeys = Object.keys(newX.reps);
          for (let i = 0; i < repKeys.length; i++) {
            const rep = newX.reps[repKeys[i]];
            if (
              existingX!.reps[repKeys[i]] != null &&
              existingX!.reps[repKeys[i]].text != rep.text
            ) {
              newX.tags.push(`#aigen-rep-${repKeys[i]}`);
            }
          }
          return newX;
        });
        console.log("suggested concepts", data);
        setFilledExistingConcepts(data);
        setSuggestedConceptsError(false);
      } else {
        console.log("error getting concepts", data);
        setSuggestedConceptsError(true);
      }
    } catch (e) {
      setSuggestedConceptsError(true);
    }
    setSuggestedConceptsLoading(false);
  }, [server, pack, creativity, quantity, key, setFilledExistingConcepts]);

  if (pack.data.concepts.length < 3) {
    return <div>Create more concepts to use AI suggestions</div>;
  }

  return (
    <span>
      {(filledExistingConcepts.length > 0 || suggestedNewConcepts.length > 0)
        ? (
          <span>
            <div
              className={`add-btn`}
              onClick={acceptSuggestedConcepts}
            >
              ✅ accept
            </div>
            &nbsp;
            <div
              className={`add-btn`}
              onClick={rejectSuggestedConcepts}
            >
              ❌ reject
            </div>
          </span>
        )
        : suggestedConceptsLoading
        ? <span className="spinner">🤖 loading...</span>
        : (
          <span>
            &nbsp;
            <div
              className={`add-btn`}
              onClick={getFillConcepts}
            >
              🤖 fill CURRENT concepts{" "}
              {suggestedConceptsError ? "(error, try again)" : ""}
            </div>{" "}
            &nbsp;
            <div
              className={`add-btn`}
              onClick={getNewConcepts}
            >
              🤖 suggest NEW concepts{" "}
              {suggestedConceptsError ? "(error, try again)" : ""}
            </div>
          </span>
        )}

      <div>
        <strong>AI Creativity:</strong>
        <label>
          <input
            type="radio"
            name="creativity"
            value="low"
            onClick={onSelectCreativity}
            checked={creativity === "low"}
          />
          <span>low</span>
        </label>
        <label>
          <input
            type="radio"
            name="creativity"
            value="medium"
            onClick={onSelectCreativity}
            checked={creativity === "medium"}
          />
          <span>medium</span>
        </label>
        <label>
          <input
            type="radio"
            name="creativity"
            value="high"
            onClick={onSelectCreativity}
            checked={creativity === "high"}
          />
          <span>high</span>
        </label>
      </div>
      <div>
        <strong>AI Suggestions:</strong>
        <label>
          <input
            type="radio"
            name="quantity"
            value="few"
            onClick={onSelectQuantity}
            checked={quantity === "few"}
          />
          <span>normal</span>
        </label>
        <label>
          <input
            type="radio"
            name="quantity"
            value="many"
            onClick={onSelectQuantity}
            checked={quantity === "many"}
          />
          <span>many</span>
        </label>
      </div>
      <div style={{ maxWidth: 240 }}>
        <strong>AI Key:</strong>
        <label>
          <input
            type="text"
            name="key"
            className="subtle"
            value={key}
            onChange={onChangeKeyText}
          />
        </label>
      </div>
      <div>
        <strong>Server:</strong>
        <label>
          <input
            type="radio"
            name="server"
            value="optimem"
            onClick={onSelectServer}
            checked={server === 'optimem'}
          />
          <span>api.optimem.org</span>
        </label>
        <label>
          <input
            type="radio"
            name="server"
            value="localhost"
            onClick={onSelectServer}
            checked={server === 'localhost'}
          />
          <span>localhost:8080</span>
        </label>
      </div>
    </span>
  );
}
